'use strict';

import { PROJECT_API } from './projectApi.js';
import { getLimits }   from './projectUpdateValidation.js';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const ALT_PLACEHOLDER = 'Describe this image';
const ALT_LABEL       = 'Image description — leave blank only if the image is decorative';

/**
 * Builds one thumbnail: the fixed-ratio tile, its remove control, the
 * description field, and the hidden id the form submits.
 *
 * Exported because collab_project.js renders the same object for images that
 * are already attached to an update, and three copies of this structure drifted
 * apart the moment one of them changed.
 *
 * The description input sits in the wrapper *beside* the tile rather than
 * inside it: the tile is a fixed 100x80 box with `overflow: hidden`
 * (%img-tile), so anything placed within it would be clipped.
 *
 * image_alt[] and image_ids[] are position-aligned by living in the same
 * wrapper — the browser submits them in document order and removing a
 * thumbnail takes the pair with it. collect_project_update_images() in
 * EditProjectUpdateApiLogic.php zips them back together server-side.
 */
export function createThumbItem({ src, imageId = null, alt = '', existing = false } = {}) {
  const item = document.createElement('div');
  item.className = 'proj-img-item';

  const tile = document.createElement('div');
  tile.className = existing ? 'proj-img-thumb proj-img-thumb--existing' : 'proj-img-thumb';

  const preview = document.createElement('img');
  preview.className = 'proj-img-thumb__img';
  preview.src = src;
  // Decorative here: this is the author's own upload shown back to them, and
  // the description they are about to type is the accessible name that matters.
  preview.alt = '';

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'proj-img-thumb__remove';
  removeBtn.setAttribute('aria-label', 'Remove image');
  removeBtn.textContent = '×';

  tile.append(preview, removeBtn);

  const altInput = document.createElement('input');
  altInput.type = 'text';
  altInput.className = 'proj-img-item__alt';
  altInput.name = 'image_alt[]';
  altInput.maxLength = 255; // MAX_PROJECT_IMAGE_ALT
  altInput.value = alt;
  altInput.placeholder = ALT_PLACEHOLDER;
  // aria-label rather than a visible <label>: the field repeats once per
  // thumbnail and its placeholder is not a substitute for a name.
  altInput.setAttribute('aria-label', ALT_LABEL);

  // A blank description is allowed — it means "decorative" — but it should be
  // a visible choice rather than an oversight, so the tile says so until the
  // author types something.
  const syncDescribed = () => item.classList.toggle('is-undescribed', altInput.value.trim() === '');
  altInput.addEventListener('input', syncDescribed);
  syncDescribed();

  item.append(tile, altInput);

  if (imageId !== null) {
    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'image_ids[]';
    hidden.value = imageId;
    item.appendChild(hidden);
  }

  return { item, tile, preview, altInput, removeBtn };
}

export function createImageUploadManager({ thumbGrid, errorEl, csrfToken, projectId, pasteZone = null }) {
  const maxImages    = getLimits().maxImages ?? 5;
  let imageCount     = thumbGrid.querySelectorAll('.proj-img-thumb').length;
  let uploadsPending = 0;

  // The removable unit is the .proj-img-item wrapper (tile + description +
  // hidden id), not the tile alone — removing only the tile would strand the
  // description and the id in the form.
  const itemOf = (el) => el.closest('.proj-img-item') || el;

  // Wire remove buttons on pre-existing thumbnails (edit forms)
  thumbGrid.querySelectorAll('.proj-img-thumb--existing').forEach(w => {
    w.querySelector('.proj-img-thumb__remove').addEventListener('click', () => {
      itemOf(w).remove(); imageCount--;
    });
  });

  // Server-rendered items (edit_project_update.mustache) come from Mustache
  // without the undescribed marker, which createThumbItem() applies for the
  // ones built here. Seed it and keep it live so both routes behave the same.
  // Runs before any thumbnail is created below, so it cannot double-bind.
  thumbGrid.querySelectorAll('.proj-img-item').forEach(item => {
    const altInput = item.querySelector('.proj-img-item__alt');
    if (!altInput) return;
    const sync = () => item.classList.toggle('is-undescribed', altInput.value.trim() === '');
    altInput.addEventListener('input', sync);
    sync();
  });

  // Wire click-to-browse on the paste zone when provided
  if (pasteZone) {
    const fileInput = document.createElement('input');
    fileInput.type     = 'file';
    fileInput.accept   = 'image/*';
    fileInput.multiple = true;
    fileInput.style.display = 'none';
    pasteZone.appendChild(fileInput);

    pasteZone.addEventListener('click', () => fileInput.click());

    // The zone is a div carrying role="button" (a real <button> cannot hold
    // the file input it owns), so the keyboard activation a button would have
    // given for free has to be supplied here. Without it, click-to-browse was
    // reachable by pointer only — Ctrl+V was the sole keyboard route to
    // attaching an image.
    pasteZone.addEventListener('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      e.preventDefault();
      fileInput.click();
    });

    fileInput.addEventListener('change', () => {
      for (const file of fileInput.files) {
        upload(file);
      }
      fileInput.value = '';
    });
  }

  function showError(msg, persistent) {
    errorEl.textContent   = msg;
    errorEl.style.display = 'block';
    if (!persistent) setTimeout(() => { errorEl.style.display = 'none'; }, 4000);
  }

  function markError(wrapper, objectUrl, msg) {
    wrapper.classList.add('proj-img-thumb--error');
    imageCount--;
    showError(msg || 'Image upload failed.');
    // A failed upload has no id to describe, so the description field would
    // submit an orphan value — drop it along with the rest of the item.
    const item = itemOf(wrapper);
    item.querySelector('.proj-img-item__alt')?.remove();
    wrapper.querySelector('.proj-img-thumb__remove').addEventListener('click', () => {
      URL.revokeObjectURL(objectUrl); item.remove();
    });
  }

  function upload(file) {
    if (imageCount >= maxImages) { showError('Maximum ' + maxImages + ' images allowed.'); return; }
    if (!ALLOWED_TYPES.includes(file.type)) { showError('Unsupported image type: ' + file.type); return; }

    imageCount++; uploadsPending++;

    const objectUrl = URL.createObjectURL(file);
    // No id yet — the hidden image_ids[] input is appended on a successful
    // response below, which is also what keeps a failed upload out of the
    // submitted set.
    const { item, tile: wrapper, removeBtn } = createThumbItem({ src: objectUrl });
    const progress  = document.createElement('div'); progress.className = 'proj-img-progress';
    const bar       = document.createElement('div'); bar.className = 'proj-img-progress__bar';
    progress.appendChild(bar);
    wrapper.appendChild(progress);
    thumbGrid.appendChild(item);

    const fd = new FormData();
    fd.append('file', file); fd.append('csrf_token', csrfToken); fd.append('project_id', projectId);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', PROJECT_API.imageUpload);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.addEventListener('progress', ev => {
      // A fraction, not a width: the bar scales on the compositor rather than
      // re-laying out on every progress event (see .proj-img-progress__bar).
      if (ev.lengthComputable) bar.style.setProperty('--upload-progress', ev.loaded / ev.total);
    });
    xhr.addEventListener('load', () => {
      uploadsPending--;
      progress.style.display = 'none';
      if (xhr.status !== 200) { markError(wrapper, objectUrl, 'Image upload failed (server error).'); return; }
      let resp; try { resp = JSON.parse(xhr.responseText); } catch (_) { markError(wrapper, objectUrl); return; }
      if (resp.return_code === '1' && resp.data && resp.data.id) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden'; hidden.name = 'image_ids[]'; hidden.value = resp.data.id;
        item.appendChild(hidden);
        removeBtn.addEventListener('click', () => { URL.revokeObjectURL(objectUrl); item.remove(); imageCount--; });
      } else {
        markError(wrapper, objectUrl, resp.error ? 'Image upload failed: ' + resp.error : 'Image upload failed.');
      }
    });
    xhr.addEventListener('error', () => {
      uploadsPending--; progress.style.display = 'none';
      markError(wrapper, objectUrl, 'Image upload failed (network error).');
    });
    xhr.send(fd);
  }

  /**
   * True when an image with this id is already present as a thumbnail
   * (uploaded, or previously picked from the project's existing images).
   */
  function hasId(id) {
    return !!thumbGrid.querySelector(`input[name="image_ids[]"][value="${CSS.escape(String(id))}"]`);
  }

  /**
   * Adds a thumbnail for an image that already exists server-side — used by
   * the "choose from project images" picker. Goes through the same
   * imageCount counter as upload(), so the MAX_IMAGES cap holds across both
   * routes; without this, attachImagesToUpdate() would silently truncate at
   * the server-side cap on save while the UI still showed every picked image.
   * Returns false when the cap is reached or the id is already present.
   */
  function attachExisting({ id, url, alt = '' }) {
    if (hasId(id)) { showError('That image is already added.'); return false; }
    if (imageCount >= maxImages) { showError('Maximum ' + maxImages + ' images allowed.'); return false; }
    imageCount++;

    // alt is prefilled from image_list.php: the same picture reused on another
    // update describes the same thing, so the author starts from the
    // description they already wrote rather than a blank field.
    const { item, removeBtn } = createThumbItem({ src: url, imageId: id, alt, existing: true });
    thumbGrid.appendChild(item);
    removeBtn.addEventListener('click', () => { item.remove(); imageCount--; });
    return true;
  }

  return {
    upload,
    attachExisting,
    hasId,
    get pending() { return uploadsPending; },
    get count()   { return imageCount; },
    hasFailed()   { return thumbGrid.querySelectorAll('.proj-img-thumb--error').length > 0; },
    pasteHandler() {
      return (e) => {
        for (const item of (e.clipboardData ? e.clipboardData.items : [])) {
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            e.preventDefault(); upload(item.getAsFile());
          }
        }
      };
    },
  };
}
